import { Group, HikStorage, Lassie, LassieMini, ShuttleRack } from '@thive/fddm-types';

const isRecord = (template: unknown): template is Record<string, unknown> =>
  typeof template === 'object' && template !== null && !Array.isArray(template);

const isString = (template: unknown): template is string => typeof template === 'string';

const isNumber = (template: unknown): template is number =>
  typeof template === 'number' && Number.isFinite(template);

const isBoolean = (template: unknown): template is boolean => typeof template === 'boolean';

const isStringArray = (template: unknown): template is string[] =>
  Array.isArray(template) && template.every(isString);

const isNumberArray = (template: unknown): template is number[] =>
  Array.isArray(template) && template.every(isNumber);

const hasAtLeastTwoStrings = (template: unknown): template is [string, string, ...string[]] =>
  isStringArray(template) && template.length >= 2;

export const isGroup = (template: unknown): template is Group =>
  isRecord(template) &&
  isString(template.id) &&
  isString(template.name) &&
  hasAtLeastTwoStrings(template.areaIds) &&
  Array.isArray(template.nestedGroups) &&
  template.nestedGroups.every(isGroup) &&
  isBoolean(template.canUngroup) &&
  isBoolean(template.canConnect);

export const isLassie = (template: unknown): template is Lassie =>
  isRecord(template) &&
  isGroup(template.group) &&
  isRecord(template.params) &&
  isNumber(template.params.numAisles) &&
  isNumber(template.params.numBays) &&
  isNumber(template.params.numShelves) &&
  isNumberArray(template.params.sprinklerShelves) &&
  Array.isArray(template.params.workstations) &&
  template.params.workstations.every(
    (workstation) =>
      isRecord(workstation) &&
      workstation.type === 'SINGLE_RAMP_TYPE_A' &&
      isNumber(workstation.positionFromBottom),
  );

export const isLassieMini = (template: unknown): template is LassieMini =>
  isRecord(template) &&
  isGroup(template.group) &&
  isRecord(template.params) &&
  isNumber(template.params.numAisles) &&
  isNumber(template.params.numBays) &&
  isNumber(template.params.numShelves);

export const isShuttleRack = (template: unknown): template is ShuttleRack =>
  isRecord(template) &&
  isGroup(template.group) &&
  isRecord(template.params) &&
  isNumber(template.params.numColumns) &&
  isNumber(template.params.numLevels) &&
  isNumber(template.params.numDeep) &&
  (template.params.entrySide === 'FRONT' || template.params.entrySide === 'FRONT_AND_BACK');

export const isHikStorage = (template: unknown): template is HikStorage =>
  isRecord(template) &&
  isGroup(template.group) &&
  isRecord(template.params) &&
  isNumber(template.params.numAisles) &&
  isNumber(template.params.numBays) &&
  isNumber(template.params.numShelves) &&
  isNumberArray(template.params.crossAislesAfterBays);

export const isTemplate = (
  template: unknown,
): template is Lassie | LassieMini | ShuttleRack | HikStorage =>
  isLassie(template) || isLassieMini(template) || isShuttleRack(template) || isHikStorage(template);
